import {map, isEqual} from 'lodash';
import getTrackLineRequests from './getTrackLineRequests';
import snapTrackPoint from './snapTrackPoint';
import debounceWithResolver from '../debounceWithResolver';
import createAsyncMutex from '../createAsyncMutex';
import fetchJSON from '../fetchJSON';
import savePoint from './savePoint';
import {AddSavePromise} from '../../hooks/useIsSaved';
import fetchElevation from './fetchElevation';
import createRemoteId, {RemoteId} from './createRemoteId';

const debouncedFetch = debounceWithResolver(window.fetch, 500);

const savedTracks: Record<string, Track> = {};
const remoteIds: Record<string, RemoteId> = {};

function getIsModifiedSinceSave(track: Track) {
    const {trackLocalId} = track.properties;
    const savedTrack = savedTracks[trackLocalId];

    // Do not save if they havent changed since the last time they were saved
    if (savedTrack && isEqual(savedTrack, track)) return false;

    // Do not save if they have already been deleted remotely
    if (savedTrack && savedTrack.properties.isLocallyDeleted) return false;

    return true;
}

export function registerSavedTrack(track: Track) {
    savedTracks[track.properties.trackLocalId] = track;
}

export function getTrackRemoteId(track: Track) {
    if (!remoteIds[track.properties.trackLocalId])  {
        remoteIds[track.properties.trackLocalId] = createRemoteId(track.properties.id);
    }
    return remoteIds[track.properties.trackLocalId].promise;
}

async function saveTrackPoints(
    track: Track,
    trackIdPromise: Promise<string>,
    project: MapProject,
    addSavePromise: AddSavePromise,
) {
    if (!track.isGeometryLoaded) return;

    Promise.all(map(track.points, async point => await savePoint({
        ...point,
        properties: {
            ...point.properties,
            track_id: await trackIdPromise,
        },
        geometry: await snapTrackPoint({point, track, project, interaction: undefined})
    }, addSavePromise)));
}

const saveTrackLines = createAsyncMutex(async (
    track: Track,
    project: MapProject,
    addSavePromise: AddSavePromise,
): Promise<string> => {

    const {trackLocalId} = track.properties;
    // Populate track's remoteId if it doesn't yet exist
    getTrackRemoteId(track);
    const remoteId = remoteIds[track.properties.trackLocalId];

    const isModifiedSinceSave = getIsModifiedSinceSave(track);
    savedTracks[trackLocalId] = track;
    if (!isModifiedSinceSave) return remoteId.promise;

    const lines = await addSavePromise(
        Promise.all(getTrackLineRequests({track, project}).map(async request => {
            let geometry = await request.geometry;
            try {
                geometry = await fetchElevation(geometry);
            } catch (error) {
                console.error(error);
            }
            return {
                type: 'Feature',
                geometry,
                properties: request.properties,
            };
        }))
    );

    const body = JSON.stringify({
        type: 'Feature',
        properties: {
            ...track.properties,
            hexcolor: track.properties.color,
        },
        geometry: {
            type: 'MultiLineString',
            coordinates: lines.map(line => line.geometry.coordinates)
        }
    });

    if (track.properties.isLocallyDeleted) {
        if (remoteId.sync) {
            window.fetch(`/api/objects/track/${remoteId.sync}/`, {
                credentials: 'same-origin',
                method: 'DELETE',
            });
        }
        delete remoteIds[track.properties.trackLocalId];

    } else if (remoteId.sync) {
        addSavePromise(debouncedFetch(`/api/objects/track/${remoteId.sync}/`, {
            method: 'PUT',
            credentials: 'same-origin',
            headers: {'Content-Type': 'application/json'},
            body,
        }));

    } else {
        const response = await addSavePromise(fetchJSON('/api/objects/track/', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {'Content-Type': 'application/json'},
            body,
        }));

        const id = response.features[0].properties.id;
        remoteId.resolve(id);
    }

    return remoteId.promise;

}, track => track.properties.trackLocalId);

export default async function saveTrack(
    track: Track,
    project: MapProject,
    addSavePromise: AddSavePromise,
): Promise<void> {

    const id = saveTrackLines(track, project, addSavePromise);
    await saveTrackPoints(track, id, project, addSavePromise);
}
